// File: frontend/src/components/admin/common/AdminCharCounter.tsx

import React from "react";
import "./AdminCharCounter.css";

interface AdminCharCounterProps {
  /** Current field value — the length shown on the left of the slash. */
  value: string;
  /** Upper bound, normally the backend column length for the same field. */
  max: number;
  /** Optional extra class for one-off placement tweaks. */
  className?: string;
}

/**
 * Small "current / max" counter rendered directly under an input or textarea
 * inside the admin panel modals.
 *
 * Every admin modal field that writes free text into a length-limited column
 * gets one, so the admin can see the remaining room before submitting instead
 * of finding out from a 500 coming back from the backend.
 *
 * The colour shifts at 90% of the limit and again once the limit is reached,
 * which is the only feedback the admin gets on fields that carry a maxLength
 * attribute — there the browser simply stops accepting keystrokes.
 */
export const AdminCharCounter: React.FC<AdminCharCounterProps> = ({
  value,
  max,
  className,
}) => {
  const length = value ? value.length : 0;
  const atLimit = max > 0 && length >= max;
  const nearLimit = !atLimit && max > 0 && length >= max * 0.9;

  const stateClass = atLimit
    ? " is-at-limit"
    : nearLimit
      ? " is-near-limit"
      : "";

  return (
    <div className={`admin-char-counter${stateClass}${className ? ` ${className}` : ""}`}>
      {length} / {max}
    </div>
  );
};